// https://github.com/microsoft/api-guidelines/blob/vNext/Guidelines.md#97-filtering
// https://learn.microsoft.com/en-us/dotnet/api/microsoft.aspnetcore.odata.query.allowedfunctions?view=odata-aspnetcore-8.0

import {z} from 'zod';

// prettier-ignore
export const comparisonOperators = ['eq', 'ne', 'gt', 'ge', 'lt', 'le', 'contains'] as const;

export const logicalOperators = ['and', 'or'] as const;
const logicalRegExp = /(?=\sand\b|\sor\b)/g; // TODO will fail if property is exactly 'and', 'or'

export function makeApiFilterParam(
  filterBy: FilterBy | FilterBy[] | null
): string | undefined {
  if (!filterBy) return;

  const construct = (f: FilterBy, idx?: number) => {
    if (idx && !f.logical) {
      // connecting logical operator required for >0 indices
      throw new TypeError('Logical operator required in list of filters');
    } else if (!idx && f.logical) {
      // connecting logical operator not supported on first item
      throw new TypeError(
        'Logical operator not supported for first filter item'
      );
    }

    const parts: string[] = [];
    const value =
      typeof f.value === 'number' || typeof f.value === 'boolean'
        ? f.value.toString()
        : typeof f.value === 'string'
          ? `'${f.value.trim()}'`
          : 'null';
    const comparison = comparisonOperators.find(
      (op) => op === f.comparison || op[0] === f.comparison
    );

    if (!comparison) throw new Error('Unrecognized filter operator');

    f.logical && parts.push(f.logical);

    // TODO write better check for this
    if (comparison === 'contains') {
      if (typeof value !== 'string')
        throw new TypeError('Value must be a string');
      parts.push(`${comparison}(toLower(${f.prop}),${value.toLowerCase()})`);
    } else {
      parts.push(f.prop, comparison as string, value);
    }

    return parts.join(' ');
  };

  if (Array.isArray(filterBy)) {
    return filterBy.map(construct).join(' ');
  } else {
    return construct(filterBy);
  }
}

export function parseApiFilterParam(param = ''): FilterBy[] | null {
  const filterStrings = param.split(logicalRegExp);

  if (!filterStrings[0]) return null;

  const filters = filterStrings.map((s) => {
    const parts = s.split(' ').filter(Boolean);
    const firstPart = parts[0] as (typeof logicalOperators)[number];
    const propIdx = logicalOperators.includes(firstPart) ? 1 : 0;
    const logical = parts[propIdx - 1] as FilterBy['logical'];

    if (parts[propIdx].includes('contains')) {
      const fnParts = parts[propIdx]
        .split(/(\(|,|\))/g)
        .filter((str) => str && !/(\(|,|toLower|\))/g.test(str));
      const comparison = fnParts[0] as FilterBy['comparison'];
      const prop = fnParts[1];

      const _value = fnParts[2];
      const value: FilterBy['value'] = !Number.isNaN(parseInt(_value))
        ? parseInt(_value)
        : _value === 'null'
          ? null
          : _value.replace(/["']/g, '');

      return {logical, comparison, prop, value};
    } else {
      const prop = parts[propIdx];
      const comparison = parts[propIdx + 1] as FilterBy['comparison'];

      const _value = parts[propIdx + 2];
      const value: string | number = !Number.isNaN(parseInt(_value))
        ? parseInt(_value)
        : _value.replace(/["']/g, '');

      return {logical, prop, comparison, value};
    }
  });

  return filters;
}

export const filterBySchema = z.object({
  prop: z.string(),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
  comparison: z.enum(comparisonOperators),
  logical: z.enum(logicalOperators).optional(),
});
export type FilterBy = z.infer<typeof filterBySchema>;
